// Money display for the current country. Amounts are stored and sent
// in that country's own currency — there is no conversion, only formatting:
// SAR/AED/QAR use 2 decimals, KWD/OMR/BHD use 3.

import { getCurrentCountry } from '@/utils/country'

type CurrencyLocale = 'en' | 'ar'

// Currency code for labels, e.g. `Price (${currencyCode()})`
export const currencyCode = (): string => getCurrentCountry().currency.code

// Symbol shown next to amounts: the ISO code in English, the Arabic symbol in Arabic
export const currencySymbol = (locale: CurrencyLocale = 'en'): string => {
  const { currency } = getCurrentCountry()

  return locale === 'ar' ? currency.symbol_ar : currency.symbol_en
}

export const currencyDecimals = (): number => getCurrentCountry().currency.decimals

// Number with the country's decimals and thousands separators, e.g. `1,234.500`
export const formatAmount = (amount: number | string | null | undefined): string => {
  const value = Number(amount)
  const decimals = currencyDecimals()

  return (Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

// Amount with the currency, e.g. `1,234.50 SAR` / `12.500 KWD`
export const formatCurrency = (amount: number | string | null | undefined, locale: CurrencyLocale = 'en'): string =>
  `${formatAmount(amount)} ${currencySymbol(locale)}`

const CURRENCY_PLACEHOLDER = /\{currency\}/g

// Dictionary strings use a `{currency}` placeholder (e.g. "Price ({currency})");
// fill it with the country's currency for the given locale
export const withCountryCurrency = <T>(value: T, locale: CurrencyLocale = 'en'): T => {
  if (typeof value === 'string') {
    return value.replace(CURRENCY_PLACEHOLDER, currencySymbol(locale)) as T
  }

  if (Array.isArray(value)) {
    return value.map(item => withCountryCurrency(item, locale)) as T
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, withCountryCurrency(item, locale)])
    ) as T
  }

  return value
}
